import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { EmptyState } from '@adornis/components/client/EmptyState.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT, GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { addPageHeaderAdditional, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { getAllInternalMartinusSchools } from '../../groups/api/getAllSchools.js';
import { Person } from '../../users/entities/person.js';
import { PermissionAPI } from '../api/PermissionAPI.js';
import {
  ACTIVE_ROLES,
  DYNAMIC_ROLES,
  GLOBAL_ROLES,
  ONLY_GLOBAL_ROLES,
  Permission,
  Role,
  ROLE_LIFECYCLES,
  type RoleLifecycle,
} from '../roles.js';

type RoleChange = { role: Role; context: string; remove: boolean };

const START_ROLE_INFO: Partial<Record<Role, string>> = {
  [Role.KANDIDAT]: 'Vor dem ersten Vertrag; ein Vertrag macht daraus Mitarbeiter.',
  [Role.SUS_BEWERBER]: 'Vor der ersten Klassenbuchung; die Buchung macht daraus Schüler.',
};

/** What a role lets its holder do - per school for the people of that school, globally for everybody. */
const ROLE_INFO: Partial<Record<Role, string>> = {
  [Role.ADMIN]: 'Voller Zugriff auf alle Daten und Funktionen, auch das Admin-Dashboard.',
  [Role.PSB]:
    'Personalsachbearbeitung: Verträge, Einstellungen, Abwesenheiten, Exporte, Rechte. Nicht zusammen mit MAV.',
  [Role.SCHULLEITER]: 'Personal-, Schüler- und Vertragsdaten der Schule; genehmigt Abwesenheiten.',
  [Role.SEKRETAER]: 'Pflegt Schüler- und Sorgeberechtigtendaten, lädt Sorgeberechtigte ein.',
  [Role.MAV]: 'Mitarbeitervertretung: sieht nur Einstellungen, die auf die MAV warten. Nicht zusammen mit PSB.',
  [Role.SUS_BEWERBER_BEARBEITUNG]: 'Bearbeitet Schülerbewerbungen und Aufnahmegespräche der Schule.',
  [Role.SPENDEN_ADMIN]: 'Verwaltet Spender und Sorgeberechtigte, sieht Spendenmandate.',
  [Role.BUCHHALTUNG]: 'Buchungen, Gruppen, Kostenstellen, SEPA-Mandate und Buchhaltung.',
  [Role.IT]: 'Sieht die Personen der Schule, verwaltet ihre Microsoft-Konten und Rollen.',
  [Role.JOB_TICKET_MANAGEMENT]: 'Erhält am 10. jedes Monats den Job-Ticket-Export per E-Mail.',
  [Role.BETA_TESTING_AI]: 'Darf den KI-Assistenten im Beta-Test nutzen.',
  [Role.WIKI]: 'Darf das interne Wiki nutzen (Testphase).',
  [Role.TICKET]: 'Veraltet und ohne Wirkung - bitte entfernen.',
};

/** The roles of a school card that are not a lifecycle stage. */
const SCHOOL_ROLES = Object.values(Role).filter(
  role => !DYNAMIC_ROLES.includes(role) && !ONLY_GLOBAL_ROLES.includes(role),
);

/** Literal classes, so Tailwind finds them: where a role stands in its lifecycle decides its colour. */
const badgeClass = (role: Role) => {
  if (ACTIVE_ROLES.includes(role)) return 'badge badge-success';
  // the applicant stage in front of the active one
  if (ROLE_LIFECYCLES.some(lifecycle => lifecycle.stages[0] === role)) return 'badge badge-info';
  if (DYNAMIC_ROLES.includes(role)) return 'badge badge-ghost';
  return 'badge badge-outline';
};

/** The furthest stage a person reached in a lifecycle; a rejected applicant ends on the rejection. */
const currentStage = (lifecycle: RoleLifecycle, has: (role: Role) => boolean): Role | undefined => {
  const furthest = lifecycle.stages.filter(has).at(-1);
  if (lifecycle.rejected && has(lifecycle.rejected) && (!furthest || furthest === lifecycle.stages[0]))
    return lifecycle.rejected;
  return furthest;
};

/** The phases every lifecycle runs through, one column each, so the lifecycles line up under each other. */
const PHASES = ['Bewerbung', 'Aktiv', 'Ehemalig'];

/** A lifecycle's stage per phase: the one before its active stage, the active one, the one after. */
const stagesByPhase = ({ stages, active }: RoleLifecycle): Array<Role | undefined> => {
  const index = stages.indexOf(active);
  return [stages[index - 1], stages[index], stages[index + 1]];
};

/**
 * Literal classes, so Tailwind finds them. The stage a person is in is filled, in the colour and with the icon of its
 * phase - a rejection ends the trip like leaving does, told apart by its icon. Stages behind them are muted and
 * ticked, the ones still ahead only outlined.
 */
const CURRENT_CHIPS = [
  { classes: 'bg-info text-info-content', icon: 'icon-[material-symbols--hourglass-top-rounded]' },
  { classes: 'bg-success text-success-content', icon: 'icon-[material-symbols--check-circle-rounded]' },
  { classes: 'bg-base-300 text-base-content', icon: 'icon-[material-symbols--history-rounded]' },
];
const REJECTED_CHIP = {
  classes: 'bg-base-300 text-base-content',
  icon: 'icon-[material-symbols--block-rounded] text-error',
};
const PASSED_CHIP = { classes: 'bg-base-200 text-base-content/70', icon: 'icon-[material-symbols--check-rounded]' };
const UPCOMING_CHIP = { classes: 'border border-dashed border-base-300 text-base-content/70', icon: null };
/** A start role that can be set: still ahead, offered with a plus. */
const ADDABLE_CHIP = { ...UPCOMING_CHIP, icon: 'icon-[material-symbols--add-rounded]' };
/** A start role that is set although the person is further along - the recalculation takes it away again. */
const OVERTAKEN_CHIP = {
  classes: 'border border-info bg-info/15 text-base-content',
  icon: 'icon-[material-symbols--hourglass-top-rounded]',
};

type ChipLook = { classes: string; icon: string | null };

/** A stage of the lifecycle track; with `toggle`, the start stage as the button that sets or removes its role. */
const stageChip = (
  role: Role,
  look: ChipLook,
  options: { current?: boolean; toggle?: { pressed: boolean; title: string; onToggle: () => void } } = {},
) => {
  const classes = `inline-flex max-w-full items-center gap-1.5 justify-self-start rounded-field px-2.5 py-1 text-sm leading-snug ${
    options.current ? 'font-semibold' : ''
  } ${look.classes}`;
  const content = html`
    ${look.icon ? html`<span class="${look.icon} size-4 shrink-0"></span>` : nothing}
    <span>${role}</span>
  `;
  return options.toggle
    ? html`<button
        type="button"
        class="${classes} cursor-pointer hover:ring-2 hover:ring-info/40"
        title=${options.toggle.title}
        aria-pressed=${options.toggle.pressed ? 'true' : 'false'}
        @click=${options.toggle.onToggle}
      >
        ${content}
      </button>`
    : html`<span class=${classes} aria-current=${options.current ? 'step' : 'false'}>${content}</span>`;
};

const loadingSkeleton = html`
  <div class="card-container">${Array.from({ length: 6 }, () => html`<div class="skeleton h-14 w-full"></div>`)}</div>
`;

export const SGUserRolesMatrix = stateful((props: { personID: Maybe<string> }) => {
  const user = useObservable(() => currentUser<Person>(), []);
  const { value: person, loading: personLoading } = useQuery(
    () => subscribeByID(Person, props.personID)({ _id: 1, roles: { name: 1, contexts: 1 } }),
    [props.personID],
  );
  const { value: schools, loading: schoolsLoading } = useQuery(() => getAllInternalMartinusSchools(), []);

  const [search, setSearch] = useState<string | null>(null);
  const [onlyActive, setOnlyActive] = useState(true);
  const [rolesToChange, setRolesToChange] = useState<RoleChange[]>([]);

  useEffect(() => {
    addPageHeaderAdditional(
      1,
      html`
        <div class="p-4 flex flex-row items-center gap-4">
          ${TextInput({
            placeholder: 'Schule suchen',
            placeholderMode: 'prefix',
            value: search ?? '',
            onChanged: value => {
              setSearch(value);
            },
          })}
          <button
            class="btn ${onlyActive ? 'btn-success' : 'btn-ghost'}"
            @click=${() => {
              setOnlyActive(!onlyActive);
            }}
          >
            Nur aktive
          </button>
        </div>
      `,
    );
    return () => {
      removePageHeaderAdditional(1);
    };
  }, [onlyActive]);

  if (schoolsLoading || personLoading) return loadingSkeleton;

  if (!person) return EmptyState({ title: 'Person nicht gefunden' });

  if (!schools?.length) return EmptyState({ title: 'Keine Schulen gefunden' });

  const canEdit = !!user?.hasPermission(Permission.CHANGE_ROLES, GLOBAL_CONTEXT) && user._id !== person._id;
  const canEditAny = !!user?.hasPermission(Permission.CHANGE_ROLES, ANY_CONTEXT) && user._id !== person._id;

  const stored = (role: Role, context: string) =>
    person.roles.some(r => r.name === role && r.contexts.includes(context));
  // an unsaved change wins over what is stored
  const has = (role: Role, context: string) => {
    const change = rolesToChange.find(c => c.role === role && c.context === context);
    return change ? !change.remove : stored(role, context);
  };
  const setRole = (role: Role, context: string, value: boolean) =>
    setRolesToChange([
      ...rolesToChange.filter(c => !(c.role === role && c.context === context)),
      ...(value === stored(role, context) ? [] : [{ role, context, remove: !value }]),
    ]);
  const hasPendingChanges = (context: string) => rolesToChange.some(c => c.context === context);

  // a person cannot be PSB and MAV at once, at any school
  const heldAnywhere = (role: Role) =>
    person.roles.some(r => r.name === role && r.contexts.length) ||
    rolesToChange.some(c => c.role === role && !c.remove);
  const excluded = (role: Role) =>
    (role === Role.PSB && heldAnywhere(Role.MAV)) || (role === Role.MAV && heldAnywhere(Role.PSB));

  // the field takes a plain label, so what the role does sits below it, indented to the label text
  const roleCheckbox = (role: Role, context: string, info: Maybe<Renderable>, enabled: boolean) => html`
    <div class="flex flex-col">
      ${CheckboxInput({
        value: has(role, context),
        disabled: !enabled,
        label: role,
        onPicked: value => setRole(role, context, !!value),
      })}
      ${info ? html`<p class="-mt-1 pb-2 pl-8 text-sm text-base-content/70">${info}</p>` : nothing}
    </div>
  `;

  const additionalRoles = (roles: Role[], context: string) => html`
    <div class="grid gap-x-6 sm:grid-cols-2 xl:grid-cols-3">
      ${repeat(
        // nobody gets the dead role anymore; somebody who still has it sees it, so it can be taken away
        roles.filter(role => role !== Role.TICKET || stored(role, context)),
        role => role,
        role => roleCheckbox(role, context, ROLE_INFO[role], canEdit && !excluded(role)),
      )}
    </div>
  `;

  const heading = (title: string, roles: Role[], context: string) => html`
    <span class="flex min-w-0 flex-wrap items-center gap-2">
      <span class="mr-2 font-semibold md:text-lg">${title}</span>
      ${repeat(
        roles,
        role => role,
        role => html`<span class=${badgeClass(role)}>${role}</span>`,
      )}
      ${hasPendingChanges(context) ? html`<span class="badge badge-warning">Ungespeichert</span>` : nothing}
    </span>
  `;

  const lifecycleRow = (lifecycle: RoleLifecycle, context: string) => {
    const current = currentStage(lifecycle, role => has(role, context));
    const rejected = !!lifecycle.rejected && current === lifecycle.rejected;
    const phases = stagesByPhase(lifecycle);
    // a rejection ends the trip in the last column, past an active phase that was never reached
    const currentPhase = rejected ? 2 : current ? phases.indexOf(current) : -1;
    const { start } = lifecycle;
    // the start role is not locked when the person is further along - the recalculation after saving removes it
    const startOvertaken = !!start && has(start, context) && current !== start;

    const cell = (phase: number) => {
      if (rejected && phase === 1) return html`<span class="text-sm text-base-content/70">nicht aufgenommen</span>`;
      if (rejected && phase === 2) return stageChip(lifecycle.rejected!, REJECTED_CHIP, { current: true });
      const stage = phases[phase];
      // keeps the column on a wide screen - Spender has no application
      if (!stage) return html`<span class="hidden md:block"></span>`;
      // the start role is set right here, by clicking it
      if (phase === 0 && start && canEdit) {
        const held = has(start, context);
        const look =
          phase === currentPhase
            ? CURRENT_CHIPS[0]!
            : startOvertaken
              ? OVERTAKEN_CHIP
              : currentPhase > 0
                ? PASSED_CHIP
                : ADDABLE_CHIP;
        return stageChip(start, look, {
          current: phase === currentPhase,
          toggle: {
            pressed: held,
            title: `${START_ROLE_INFO[start] ?? ''} Klicken zum ${held ? 'Entfernen' : 'Eintragen'}.`,
            onToggle: () => setRole(start, context, !held),
          },
        });
      }
      if (phase === currentPhase) return stageChip(stage, CURRENT_CHIPS[phase]!, { current: true });
      return stageChip(stage, phase < currentPhase ? PASSED_CHIP : UPCOMING_CHIP);
    };
    // the way already walked is darker than the way ahead; no arrow out of a missing phase
    const arrow = (toPhase: number) =>
      phases[toPhase - 1]
        ? html`<span
            class="icon-[material-symbols--arrow-forward-rounded] size-5 shrink-0 justify-self-center ${
              toPhase <= currentPhase && !rejected ? 'text-base-content/70' : 'text-base-content/30'
            }"
          ></span>`
        : html`<span class="hidden md:block"></span>`;

    return html`
      <div class="flex flex-col gap-3 py-3 md:col-span-full md:grid md:grid-cols-subgrid md:items-center">
        <div>
          <p class="font-semibold">${lifecycle.label}</p>
          ${lifecycle.derivedFrom ? html`<p class="text-sm text-base-content/70">${lifecycle.derivedFrom}</p>` : nothing}
        </div>
        <!-- one wrapping line on a phone; from md on the stages are the phase columns of the grid -->
        <div class="flex flex-wrap items-center gap-2 md:contents">
          ${cell(0)} ${arrow(1)} ${cell(1)} ${arrow(2)} ${cell(2)}
        </div>
        ${
          startOvertaken
            ? html`<p class="text-sm text-warning-content md:col-span-5 md:col-start-2">
                ${start} wird beim Speichern wieder entfernt - bereits ${current}.
              </p>`
            : nothing
        }
      </div>
    `;
  };

  const schoolHasRoles = (schoolID: string) =>
    person.roles.some(role => role.contexts.includes(schoolID)) ||
    rolesToChange.some(c => c.context === schoolID && !c.remove);

  // search is not done via api as we already loaded all schools anyway. Also there are not that many schools
  const visibleSchools = schools.filter(
    school =>
      school.name?.toLowerCase().includes((search ?? '').toLowerCase()) && (!onlyActive || schoolHasRoles(school._id)),
  );
  const globalRoles = GLOBAL_ROLES.filter(role => has(role, GLOBAL_CONTEXT));
  const showGlobal = !onlyActive || globalRoles.length > 0 || hasPendingChanges(GLOBAL_CONTEXT);

  const save = () =>
    guardDialog(
      async () => {
        // one call per role and direction, with all its contexts
        const grouped = new Map<string, { role: Role; contexts: string[]; remove: boolean }>();
        for (const { role, context, remove } of rolesToChange) {
          const key = `${role}:${remove}`;
          grouped.set(key, { role, remove, contexts: [...(grouped.get(key)?.contexts ?? []), context] });
        }
        for (const { role, contexts, remove } of grouped.values()) {
          await PermissionAPI.saveRoleForPerson(person._id, role, contexts, remove)();
        }
        // moves the lifecycles on from a start role set by hand, and takes it away again where it was overtaken
        await PermissionAPI.recalculateForPerson(person._id)();
      },
      {
        success: () => {
          setRolesToChange([]);
          SgSnackbar.show('Rollen gespeichert und neu berechnet');
        },
      },
    );

  return html`
    <div class="card-container">
      ${!showGlobal && !visibleSchools.length ? EmptyState({ title: 'Keine aktiven Rollen gefunden' }) : nothing}
      ${
        showGlobal
          ? Collapse({
              heading: heading('Global', globalRoles, GLOBAL_CONTEXT),
              content: () => additionalRoles(GLOBAL_ROLES, GLOBAL_CONTEXT),
            })
          : nothing
      }
      ${repeat(
        visibleSchools,
        school => school._id,
        school =>
          Collapse({
            heading: heading(
              school.name ?? school._id,
              [
                ...ROLE_LIFECYCLES.map(lifecycle => currentStage(lifecycle, role => has(role, school._id))),
                ...SCHOOL_ROLES.filter(role => has(role, school._id)),
              ].filter((role): role is Role => !!role),
              school._id,
            ),
            defaultOpen: visibleSchools.length === 1,
            content: () => html`
              <div class="flex flex-col gap-6 pt-2">
                <section class="flex flex-col">
                  <p class="text-sm font-semibold text-base-content/70">Lebenszyklus</p>
                  <div
                    class="flex flex-col divide-y divide-base-300 md:grid md:grid-cols-[minmax(0,10rem)_repeat(5,auto)_1fr] md:gap-x-3"
                  >
                    <div class="hidden pb-2 md:col-span-full md:grid md:grid-cols-subgrid">
                      <span></span>
                      ${PHASES.map(
                        (phase, index) => html`
                          ${index ? html`<span></span>` : nothing}
                          <p class="text-sm font-semibold text-base-content/70">${phase}</p>
                        `,
                      )}
                    </div>
                    ${ROLE_LIFECYCLES.map(lifecycle => lifecycleRow(lifecycle, school._id))}
                  </div>
                </section>
                <section class="flex flex-col gap-2">
                  <p class="text-sm font-semibold text-base-content/70">Zusätzliche Rollen</p>
                  ${additionalRoles(SCHOOL_ROLES, school._id)}
                </section>
              </div>
            `,
          }),
      )}
      <div class="h-64"></div>
    </div>
    ${
      canEdit || canEditAny
        ? html`
            <div
              class="fixed bottom-0 z-20 flex justify-end gap-4 p-4 border-t border-base-300 bg-base-100/80 shadow-[0_-8px_24px_-12px_rgb(16_42_49/0.18)] backdrop-blur-md backdrop-saturate-150 ${
                navigator.userAgent.toLowerCase().includes('firefox')
                  ? 'w-[-moz-available]'
                  : 'w-[-webkit-fill-available]'
              }"
            >
              <button
                class="btn btn-ghost btn-lg"
                @click=${() =>
                  guardDialog(() => PermissionAPI.recalculateForPerson(person._id)(), {
                    success: () => SgSnackbar.show('Rollen erfolgreich neu berechnet'),
                  })}
              >
                <span class="icon-[material-symbols--refresh-outline-rounded] size-5"></span>
                Rollen neu berechnen
              </button>
              ${
                canEdit
                  ? html`<button class="btn btn-primary btn-lg" ?disabled=${!rolesToChange.length} @click=${save}>
                      <span class="icon-[material-symbols--save-outline-rounded] size-5"></span>
                      Speichern
                    </button>`
                  : nothing
              }
            </div>
          `
        : nothing
    }
  `;
});
